/* ============================================================
   CLEARANCE GUYS — cart behaviour.

   Four jobs, all additive to Dawn (cart.js / cart-drawer.js are
   untouched and keep owning add / quantity / remove / re-render):

   1. Free-delivery bar  — the bar itself is rendered in Liquid
      (snippets/cg-cart-bar.liquid) inside the regions Dawn
      re-renders, so the numbers are always server-truth. This
      file only mirrors the message into a persistent aria-live
      region (a re-created live node never announces) and adds the
      one-off flash when the bar first hits 100%.
   2. "Grab one more" upsell — desktop only. Fetches the
      complementary recommendations for the first line item through
      sections/cg-cart-upsell.liquid and re-hydrates after every
      drawer re-render.
   3. Discount code on /cart — tries the Ajax cart API, falls back
      to /checkout?discount=CODE.
   4. Optional undo toast when a line is removed on /cart.
   ============================================================ */

(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 750px)';
  var UNDO_TIMEOUT = 5000;

  var strings = {
    discountError: 'That code is not valid',
    itemRemoved: 'Removed from your basket',
    undo: 'Undo',
  };

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function postJSON(url, data) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /* ----------------------------------------------------------
     1. Free-delivery bar: live region + completion flash
     ---------------------------------------------------------- */

  var liveRegion = null;
  var lastMessage = null;
  var lastComplete = null;

  function getLiveRegion() {
    if (liveRegion && liveRegion.isConnected) return liveRegion;
    liveRegion = document.getElementById('CGCartStatus');
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.id = 'CGCartStatus';
      liveRegion.className = 'cg-visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    return liveRegion;
  }

  function syncDeliveryBars(firstRun) {
    var bars = document.querySelectorAll('[data-cg-cart-bar]');
    if (!bars.length) {
      lastMessage = null;
      lastComplete = null;
      return;
    }

    var textEl = bars[0].querySelector('[data-cg-cart-bar-text]');
    var message = textEl ? textEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var complete = bars[0].getAttribute('data-cg-cart-bar-complete') === 'true';

    if (message && message !== lastMessage) {
      if (!firstRun) getLiveRegion().textContent = message;
      lastMessage = message;
    }

    if (complete && lastComplete === false && !reducedMotion()) {
      Array.prototype.forEach.call(bars, function (bar) {
        bar.classList.remove('cg-cart-bar--flash');
        void bar.offsetWidth;
        bar.classList.add('cg-cart-bar--flash');
      });
    }

    lastComplete = complete;
  }

  /* ----------------------------------------------------------
     2. Drawer upsell — "Grab one more"
     ---------------------------------------------------------- */

  var upsellCache = {};

  function hydrateUpsell() {
    var host = document.querySelector('[data-cg-upsell]');
    if (!host) return;

    if (!window.matchMedia || !window.matchMedia(DESKTOP_QUERY).matches) {
      host.hidden = true;
      return;
    }

    var productId = host.getAttribute('data-cg-upsell-product');
    var url = host.getAttribute('data-cg-upsell-url');
    if (!productId || !url) {
      host.hidden = true;
      return;
    }

    // Idempotent: never write the same content twice, so the
    // MutationObserver below cannot feed itself.
    if (host.getAttribute('data-cg-upsell-rendered') === productId) return;

    if (Object.prototype.hasOwnProperty.call(upsellCache, productId)) {
      paintUpsell(host, productId, upsellCache[productId]);
      return;
    }

    host.setAttribute('data-cg-upsell-rendered', productId);

    fetch(
      url +
        '?section_id=cg-cart-upsell&intent=complementary&limit=10&product_id=' +
        encodeURIComponent(productId)
    )
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        var html = '';
        if (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var inner = doc.querySelector('[data-cg-upsell-inner]');
          if (inner && inner.children.length) html = inner.innerHTML;
        }
        upsellCache[productId] = html;
        var current = document.querySelector('[data-cg-upsell]');
        if (current) paintUpsell(current, productId, html);
      })
      .catch(function () {
        upsellCache[productId] = '';
      });
  }

  function paintUpsell(host, productId, html) {
    host.setAttribute('data-cg-upsell-rendered', productId);
    if (!html) {
      host.hidden = true;
      if (host.innerHTML !== '') host.innerHTML = '';
      return;
    }
    if (host.innerHTML !== html) host.innerHTML = html;
    host.hidden = false;
  }

  function onUpsellAdd(button) {
    var variantId = button.getAttribute('data-cg-upsell-variant');
    var drawer = document.querySelector('cart-drawer');
    if (!variantId || !drawer) return;

    button.disabled = true;

    var sections = drawer.getSectionsToRender
      ? drawer.getSectionsToRender().map(function (section) {
          return section.id;
        })
      : ['cart-drawer', 'cart-icon-bubble'];

    postJSON(window.routes.cart_add_url, {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sections,
      sections_url: window.location.pathname,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (result) {
        if (!result || result.status) throw new Error('add failed');
        drawer.renderContents(result);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cg-cart-upsell', productVariantId: variantId });
        }
      })
      .catch(function () {
        button.disabled = false;
      });
  }

  /* ----------------------------------------------------------
     3. Discount code (/cart)
     ---------------------------------------------------------- */

  function applyDiscount(form) {
    var input = form.querySelector('[data-cg-discount-input]');
    var button = form.querySelector('[data-cg-discount-apply]');
    var errorEl = form.querySelector('[data-cg-discount-error]');
    var code = input ? input.value.trim() : '';

    if (errorEl) errorEl.textContent = '';
    if (!code) {
      if (input) input.focus();
      return;
    }

    if (button) button.disabled = true;

    function fallback() {
      window.location.assign(root() + 'checkout?discount=' + encodeURIComponent(code));
    }

    postJSON(window.routes.cart_update_url, { discount: code })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        var codes = cart && cart.discount_codes;
        if (!Array.isArray(codes)) {
          // This API version ignores `discount` on /cart/update.js.
          fallback();
          return;
        }

        var match = null;
        for (var i = 0; i < codes.length; i++) {
          if (String(codes[i].code || '').toLowerCase() === code.toLowerCase()) {
            match = codes[i];
            break;
          }
        }

        if (!match) {
          fallback();
          return;
        }

        if (match.applicable === false) {
          if (errorEl) errorEl.textContent = form.getAttribute('data-cg-discount-message') || strings.discountError;
          if (button) button.disabled = false;
          if (input) input.focus();
          return;
        }

        // Applied. Totals, line prices and the delivery bar all move,
        // so take the server's word for the whole page.
        window.location.assign(window.location.pathname);
      })
      .catch(fallback);
  }

  /* ----------------------------------------------------------
     4. Undo toast (/cart only)
     ---------------------------------------------------------- */

  var pendingUndo = null;
  var toastTimer = null;

  function showUndoToast(item) {
    dismissToast();

    var toast = document.createElement('div');
    toast.className = 'cg-cart-toast';
    toast.id = 'CGCartToast';
    toast.setAttribute('role', 'status');

    var label = document.createElement('span');
    label.textContent = item.title || strings.itemRemoved;
    toast.appendChild(label);

    var undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'cg-cart-toast__undo';
    undo.textContent = item.undoLabel || strings.undo;
    undo.addEventListener('click', function () {
      undo.disabled = true;
      postJSON(window.routes.cart_add_url, { items: [{ id: Number(item.id), quantity: Number(item.quantity) }] })
        .then(function () {
          window.location.assign(window.location.pathname);
        })
        .catch(function () {
          dismissToast();
        });
    });
    toast.appendChild(undo);

    document.body.appendChild(toast);
    toastTimer = window.setTimeout(dismissToast, UNDO_TIMEOUT);
  }

  function dismissToast() {
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = null;
    var existing = document.getElementById('CGCartToast');
    if (existing) existing.remove();
  }

  /* ----------------------------------------------------------
     Wiring
     ---------------------------------------------------------- */

  function hydrate(firstRun) {
    syncDeliveryBars(firstRun === true);
    hydrateUpsell();
  }

  var scheduled = false;
  function scheduleHydrate() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      hydrate(false);
    }, 0);
  }

  function init() {
    hydrate(true);

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var addButton = target.closest('[data-cg-upsell-add]');
      if (addButton) {
        event.preventDefault();
        onUpsellAdd(addButton);
        return;
      }

      var undoTrigger = target.closest('[data-cg-undo-id]');
      if (undoTrigger) {
        pendingUndo = {
          id: undoTrigger.getAttribute('data-cg-undo-id'),
          quantity: undoTrigger.getAttribute('data-cg-undo-qty') || 1,
          title: undoTrigger.getAttribute('data-cg-undo-label') || strings.itemRemoved,
          undoLabel: undoTrigger.getAttribute('data-cg-undo-text') || strings.undo,
        };
      }
    });

    document.addEventListener('submit', function (event) {
      var form = event.target && event.target.closest ? event.target.closest('[data-cg-discount]') : null;
      if (!form) return;
      event.preventDefault();
      applyDiscount(form);
    });

    // Dawn replaces whole regions of the drawer through several code
    // paths (renderContents, updateQuantity, onCartUpdate) and not all
    // of them publish afterwards, so watch the DOM as well as the bus.
    var drawer = document.querySelector('cart-drawer');
    if (drawer && window.MutationObserver) {
      new MutationObserver(scheduleHydrate).observe(drawer, { childList: true, subtree: true });
    }

    var cartItems = document.querySelector('cart-items');
    if (cartItems && window.MutationObserver) {
      new MutationObserver(scheduleHydrate).observe(cartItems, { childList: true, subtree: true });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        scheduleHydrate();
        if (pendingUndo) {
          var item = pendingUndo;
          pendingUndo = null;
          if (item.id) showUndoToast(item);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
